import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import ReportCharts from "../components/ReportCharts";
import { Trophy, Plus, Trash2, X } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth, isFieldRole } from "../context/AuthContext";

const WINDOWS = [
  { id: "q1", label: "Qtr 1 Program" },
  { id: "q2", label: "Qtr 2 Program" },
  { id: "q3", label: "Qtr 3 Program" },
  { id: "q4", label: "Qtr 4 Program" },
  { id: "annual", label: "Annual Program" },
];

// Point 41 — derive the milestone "window" from a configured Program (Configuration engine).
function programWindow(p) {
  if (!p) return "annual";
  if (p.kind === "annual" || /annual/i.test(p.code || "")) return "annual";
  const mq = (p.code || "").match(/Q\s*(\d)/i);
  return mq ? `q${mq[1]}` : "q1";
}

const monthOf = (s) => (s ? String(s).slice(0, 7) : null);
const unlockCharts = [
  { id: "ms-trend", type: "area", title: "Unlocks over time", subtitle: "Monthly milestone unlocks", valueLabel: "Unlocks", groupBy: (r) => monthOf(r.unlocked_at), sort: "time", color: "#7c3aed" },
  { id: "ms-window", type: "donut", title: "By program window", valueLabel: "Unlocks", groupBy: (r) => (r.window === "quarterly" ? "q1" : r.window) },
  { id: "ms-reward", type: "bar", title: "Top rewards unlocked", valueLabel: "Unlocks", groupBy: (r) => r.reward_label, sort: "value_desc", limit: 10, labelWidth: 130 },
];

export default function Milestones() {
  const { user } = useAuth();
  const readOnly = isFieldRole(user?.role); // DSE / Zonal — view-only, no add/edit
  const [slabs, setSlabs] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ window: "q1", program_id: "", name: "", threshold_points: 1000, reward_label: "", active: true, start_date: "", end_date: "", carry_forward_allowed: false });

  async function fetchData() {
    const [a, b, c] = await Promise.all([api.get("/milestones/slabs"), api.get("/milestones/unlocks"), api.get("/programs")]);
    setSlabs(a.data); setUnlocks(b.data); setPrograms(c.data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, b, c] = await Promise.all([api.get("/milestones/slabs"), api.get("/milestones/unlocks"), api.get("/programs")]);
        if (alive) { setSlabs(a.data); setUnlocks(b.data); setPrograms(c.data); }
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  // Point 41 — open the New-Slab form pre-selecting the first configured program.
  function openNew() {
    const first = programs[0];
    setForm((f) => ({ ...f, program_id: first?.id || "", window: programWindow(first) }));
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    try { await api.post("/milestones/slabs", form); setOpen(false); await fetchData(); }
    catch (e) { alert(formatError(e)); }
  }

  async function del(id, e) {
    e?.stopPropagation();
    if (!window.confirm("Delete slab?")) return;
    await api.delete(`/milestones/slabs/${id}`); await fetchData();
  }

  const unlockCols = [
    { key: "unlocked_at", label: "Date", render: (r) => new Date(r.unlocked_at).toLocaleDateString() },
    { key: "physio_name", label: "Physio", filter: "text", render: (r) => <span className="text-[12px] text-slate-800">{r.physio_name || "—"}</span> },
    { key: "physio_mobile", label: "Mobile", filter: "text", render: (r) => <span className="font-mono text-[11px] text-slate-600">{r.physio_mobile || "—"}</span> },
    { key: "physio_erp", label: "ERP ID", filter: "text", render: (r) => <span className="font-mono text-[11px] text-slate-600">{r.physio_erp || "—"}</span> },
    { key: "window", label: "Window", filter: "select", options: ["q1", "annual"], render: (r) => {
      const lbl = r.window === "q1" || r.window === "quarterly" ? "Qtr 1 Program" : r.window === "annual" ? "Annual Program" : "—";
      return <Tag tone="slate">{lbl}</Tag>;
    } },
    { key: "reward_label", label: "Reward", filter: "text" },
    { key: "status", label: "Status", filter: "select", options: ["unlocked", "in_transit", "delivered"],
      render: (r) => <Tag tone={r.status === "delivered" ? "emerald" : r.status === "in_transit" ? "amber" : "teal"}>{r.status}</Tag> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Loyalty Engine"
        title="Milestone Engine"
        subtitle="Configurable, multi-window milestone slabs with bonus boosters — and a sortable, filterable unlocks ledger for ops review."
        testId="milestones-page"
        actions={readOnly ? null : <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="add-slab-btn"><Plus size={14} /> Add slab</button>}
      />

      {/* Point 41 — only show sections for the ACTIVE programs configured in the
          Configuration engine (no stray inactive Q3/Q4 windows). */}
      {programs.map((p) => {
        const win = programWindow(p);
        const list = slabs.filter((s) => (s.program_id === p.id) || (!s.program_id && (s.window === win || (win === "q1" && s.window === "quarterly"))))
          .sort((a, b) => (a.threshold_points || 0) - (b.threshold_points || 0));
        return (
          <Card key={p.id} title={`${p.name} slabs`} subtitle={`${p.code} · ${list.length} configuration${list.length === 1 ? "" : "s"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.length === 0 && <div className="text-[12px] text-slate-400 col-span-full py-2">No slabs configured for this program yet.</div>}
              {list.map((s) => (
                <div key={s.id} className="surface p-4 surface-hover" data-testid={`slab-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2"><Trophy size={14} className="text-teal-700" /><div className="font-medium text-[14px]">{s.name}</div></div>
                      <div className="text-[12px] text-slate-500 mt-1">{s.reward_label}</div>
                    </div>
                    {!readOnly && <button onClick={(e) => del(s.id, e)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-slab-${s.id}`}><Trash2 size={14} /></button>}
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <div><div className="serif-display text-3xl numerals">{fmt(s.threshold_points)}</div><div className="small-caps text-slate-400">threshold</div></div>
                  </div>
                  {(s.start_date || s.end_date || s.carry_forward_allowed) && (
                    <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap gap-1.5 text-[10.5px]">
                      {s.start_date && <Tag tone="slate">From {s.start_date}</Tag>}
                      {s.end_date && <Tag tone="slate">To {s.end_date}</Tag>}
                      {s.carry_forward_allowed && <Tag tone="emerald">Carry-fwd</Tag>}
                    </div>
                  )}
                </div>
              ))}
              {list.length === 0 && <div className="text-slate-400 text-[12px]">No slabs in this window.</div>}
            </div>
          </Card>
        );
      })}

      {unlocks.length > 0 && <ReportCharts rows={unlocks} charts={unlockCharts} testId="milestones" title="Unlock insights" />}

      <Card title="Recent milestone unlocks">
        <DataTable columns={unlockCols} data={unlocks} rowKey="id" searchableKeys={["physio_name", "physio_mobile", "physio_erp", "reward_label"]} exportFilename="cbc-milestone-unlocks" testIdPrefix="unlocks" initialSort={{ key: "unlocked_at", dir: "desc" }} />
      </Card>

      {open && !readOnly && (
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="add-slab-modal">
            <div className="flex items-center justify-between"><h3 className="serif-display text-2xl">New milestone slab</h3><button type="button" onClick={() => setOpen(false)}><X size={18} className="text-slate-400" /></button></div>
            <div><label className="small-caps text-slate-500">Program</label>
              <select value={form.program_id} onChange={(e) => { const p = programs.find((x) => x.id === e.target.value); setForm({ ...form, program_id: e.target.value, window: programWindow(p) }); }} className="w-full" data-testid="slab-program" required>
                <option value="" disabled>Select a program…</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code}){p.active ? "" : " · inactive"}</option>)}
              </select>
              <div className="text-[11px] text-slate-400 mt-1">Programs are configured in the Configuration engine. Window: <span className="font-medium capitalize">{form.window}</span></div>
            </div>
            <div><label className="small-caps text-slate-500">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="slab-name" /></div>
            <div><label className="small-caps text-slate-500">Threshold (points)</label><input type="number" value={form.threshold_points} onChange={(e) => setForm({ ...form, threshold_points: Number(e.target.value) })} required data-testid="slab-threshold" /></div>
            <div><label className="small-caps text-slate-500">Reward label</label><input value={form.reward_label} onChange={(e) => setForm({ ...form, reward_label: e.target.value })} required data-testid="slab-reward-label" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="small-caps text-slate-500">Start date</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="slab-start-date" />
              </div>
              <div>
                <label className="small-caps text-slate-500">End date</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="slab-end-date" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer surface p-2">
              <input type="checkbox" checked={form.carry_forward_allowed} onChange={(e) => setForm({ ...form, carry_forward_allowed: e.target.checked })} data-testid="slab-carry-forward" />
              <span><span className="font-medium">Carry-forward allowed</span> <span className="text-slate-500">— unused points roll into the next window</span></span>
            </label>
            <button type="submit" className="btn-primary w-full">Create slab</button>
          </form>
        </div>
      )}
    </div>
  );
}
